import React, { useState, useEffect } from "react";
import axios from "axios";
import "./familyStudent.css";
import "./dashboard.css";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import {
  FiSearch,
  FiUser,
  FiUsers,
  FiHome,
  FiCreditCard,
  FiPhone,
  FiMail,
  FiCalendar,
  FiBook,
  FiPlus,
  FiSave,
  FiCheck,
  FiArrowRight,
  FiChevronRight,
  FiChevronLeft,
  FiRefreshCw,
  FiX,
  FiEdit,
  FiAlertCircle,
} from "react-icons/fi";

// const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
export const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
const AddFamilyStudent = () => {
  const [familyCode, setFamilyCode] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [cnic, setCnic] = useState("");
  const [familyCodeSuggestions, setFamilyCodeSuggestions] = useState([]);
  const [familyNamesSuggestions, setFamilyNamesSuggestions] = useState([]);
  const [cnicSuggestions, setCnicSuggestions] = useState([]);
  const [familiesData, setFamiliesData] = useState(null);
  const [isSearchDisabled, setIsSearchDisabled] = useState(false);
  const [students, setStudents] = useState([]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Missing students tracking
  const [missingStudentsInfo, setMissingStudentsInfo] = useState({
    missingBoys: 0,
    missingGirls: 0,
    registeredBoys: 0,
    registeredGirls: 0,
    isComplete: false,
    totalBoys: 0,
    totalGirls: 0,
  });

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const classShortForms = {
    "Play Group": "PG",
    Nursery: "Nur",
    Prep: "Prep",
    "Class 1": "1",
    "Class 2": "2",
    "Class 3": "3",
    "Class 4": "4",
    "Class 5": "5",
    "Class 6": "6",
    "Class 7": "7",
    "Class 8": "8",
    "Class Pre 9": "P9",
    "Class 9": "9",
    "Class 10": "10",
  };

  // ========== PHP BACKEND INTEGRATION ==========

  // Fetch sections from PHP backend
  const fetchSections = async (className, studentIndex) => {
    if (!className) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=get-sections-by-class&class=${encodeURIComponent(
          className
        )}`
      );
      if (response.data.sections && Array.isArray(response.data.sections)) {
        setStudents((prev) =>
          prev.map((student, idx) =>
            idx === studentIndex
              ? {
                  ...student,
                  availableSections: response.data.sections,
                  section: "",
                }
              : student
          )
        );
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sections for selected class",
      });
    }
  };

  // Search for families using PHP backend
  const searchFamilies = async () => {
    if (!familyCode && !familyName && !cnic) {
      Swal.fire({
        icon: "warning",
        title: "Search Criteria Required",
        text: "Please enter at least one search criterion (Family Code, Family Name, or CNIC).",
      });
      return;
    }

    setIsLoading(true);
    try {
      // First check if family exists (for new families)
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=check-family-duplicate`,
        {
          params: {
            family_code: familyCode || undefined,
            family_name: familyName || undefined,
            cnic: cnic || undefined,
          },
        }
      );

      const {
        exists,
        family,
        missing_boys,
        missing_girls,
        is_complete,
        registered_boys,
        registered_girls,
      } = response.data;

      if (exists) {
        setFamiliesData(family);

        // Store missing students info
        setMissingStudentsInfo({
          missingBoys: missing_boys || 0,
          missingGirls: missing_girls || 0,
          registeredBoys: registered_boys || 0,
          registeredGirls: registered_girls || 0,
          isComplete: is_complete || false,
          totalBoys: family.boys || 0,
          totalGirls: family.girls || 0,
        });

        if (is_complete) {
          // All students already registered
          Swal.fire({
            title: "Family Registration Complete",
            html: `
              <div style="text-align: left;">
                <p><strong>Family Code:</strong> ${family.family_code}</p>
                <p><strong>Family Name:</strong> ${family.family_name}</p>
                <p><strong>Registered Boys:</strong> ${registered_boys} of ${family.boys}</p>
                <p><strong>Registered Girls:</strong> ${registered_girls} of ${family.girls}</p>
                <p><strong>Status:</strong> All students are already registered.</p>
                <p class="text-warning" style="color: #f59e0b; margin-top: 10px;">
                  <FiAlertCircle style="vertical-align: middle; margin-right: 5px;" />
                  If you need to add more students, please update the family record first.
                </p>
              </div>
            `,
            icon: "info",
            confirmButtonText: "OK",
            width: 600,
          });
          setStudents([]); // Clear any existing student forms
        } else {
          // Has missing students - ask how many to add
          Swal.fire({
            title: "Family Found - Missing Students",
            html: `
              <div style="text-align: left;">
                <p><strong>Family Code:</strong> ${family.family_code}</p>
                <p><strong>Total Boys:</strong> ${family.boys} (${registered_boys} registered, ${missing_boys} missing)</p>
                <p><strong>Total Girls:</strong> ${family.girls} (${registered_girls} registered, ${missing_girls} missing)</p>
                <p>How many missing students do you want to register now?</p>
                <div style="margin: 15px 0;">
                  <label style="display: block; margin-bottom: 5px; font-weight: 500;">Boys to add (max: ${missing_boys}):</label>
                  <input type="number" id="add-boys" value="${missing_boys}" min="0" max="${missing_boys}" style="width: 100px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                </div>
                <div style="margin: 15px 0;">
                  <label style="display: block; margin-bottom: 5px; font-weight: 500;">Girls to add (max: ${missing_girls}):</label>
                  <input type="number" id="add-girls" value="${missing_girls}" min="0" max="${missing_girls}" style="width: 100px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                </div>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Create Registration Forms",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            width: 500,
            preConfirm: () => {
              const addBoys =
                parseInt(document.getElementById("add-boys").value) || 0;
              const addGirls =
                parseInt(document.getElementById("add-girls").value) || 0;

              if (addBoys < 0 || addGirls < 0) {
                Swal.showValidationMessage("Values cannot be negative");
                return false;
              }
              if (addBoys > missing_boys) {
                Swal.showValidationMessage(
                  `Cannot add more than ${missing_boys} boys`
                );
                return false;
              }
              if (addGirls > missing_girls) {
                Swal.showValidationMessage(
                  `Cannot add more than ${missing_girls} girls`
                );
                return false;
              }
              if (addBoys === 0 && addGirls === 0) {
                Swal.showValidationMessage("Please add at least one student");
                return false;
              }

              return { addBoys, addGirls };
            },
          }).then((result) => {
            if (result.isConfirmed) {
              const { addBoys, addGirls } = result.value;
              createStudentForms(addBoys, addGirls, family);
            }
          });
        }
      } else {
        // New family - ask for number of students
        Swal.fire({
          title: "New Family Registration",
          html: `
            <div style="text-align: left;">
              <p>No existing family found with the search criteria.</p>
              <p>Would you like to create a new family?</p>
            </div>
          `,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, Create New Family",
          cancelButtonText: "No, Cancel",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        }).then((createResult) => {
          if (createResult.isConfirmed) {
            // Ask for number of boys
            Swal.fire({
              title: "New Family - Boys Count",
              text: "How many boys in the family?",
              input: "number",
              inputPlaceholder: "Enter number of boys",
              inputValue: 0,
              showCancelButton: true,
              confirmButtonText: "Next",
              cancelButtonText: "Cancel",
              inputValidator: (value) => {
                if (value === "" || value === null) {
                  return "Please enter number of boys";
                }
                if (parseInt(value) < 0) {
                  return "Number cannot be negative";
                }
              },
            }).then((boysResult) => {
              if (boysResult.isConfirmed) {
                const boys = parseInt(boysResult.value) || 0;
                // Ask for number of girls
                Swal.fire({
                  title: "New Family - Girls Count",
                  text: "How many girls in the family?",
                  input: "number",
                  inputPlaceholder: "Enter number of girls",
                  inputValue: 0,
                  showCancelButton: true,
                  confirmButtonText: "Create Family",
                  cancelButtonText: "Back",
                  inputValidator: (value) => {
                    if (value === "" || value === null) {
                      return "Please enter number of girls";
                    }
                    if (parseInt(value) < 0) {
                      return "Number cannot be negative";
                    }
                  },
                }).then((girlsResult) => {
                  if (girlsResult.isConfirmed) {
                    const girls = parseInt(girlsResult.value) || 0;

                    if (boys === 0 && girls === 0) {
                      Swal.fire({
                        icon: "warning",
                        title: "No Students",
                        text: "Family must have at least one student",
                      });
                      return;
                    }

                    const familyData = {
                      family_code:
                        familyCode || `FAM-${Date.now().toString().slice(-6)}`,
                      family_name: familyName || "New Family",
                      primary_mobile: "",
                      address: "",
                      cnic: cnic || "",
                      boys: boys,
                      girls: girls,
                      total_students: boys + girls,
                    };
                    setFamiliesData(familyData);

                    // For new family, all students are missing initially
                    setMissingStudentsInfo({
                      missingBoys: boys,
                      missingGirls: girls,
                      registeredBoys: 0,
                      registeredGirls: 0,
                      isComplete: false,
                      totalBoys: boys,
                      totalGirls: girls,
                    });

                    createStudentForms(boys, girls, familyData);
                  }
                });
              }
            });
          }
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      Swal.fire({
        icon: "error",
        title: "Search Failed",
        text: error.response?.data?.error || "Failed to search for families.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Create student forms based on boys/girls count
  const createStudentForms = (boysCount, girlsCount, familyData) => {
    const newStudents = [];

    // Create boy students
    for (let i = 0; i < boysCount; i++) {
      newStudents.push({
        id: Date.now() + i,
        name: "",
        fatherName: familyData?.family_name || "",
        cnic: familyData?.cnic || "",
        mobile: familyData?.primary_mobile || "",
        gender: "Male",
        address: familyData?.address || "",
        class: "",
        section: "",
        studentId: "",
        familyCode: familyData?.family_code || "",
        availableSections: [],
        bFormNo: familyData?.cnic || "",
        familyStatus: "Family",
        isSaved: false,
      });
    }

    // Create girl students
    for (let i = 0; i < girlsCount; i++) {
      newStudents.push({
        id: Date.now() + boysCount + i,
        name: "",
        fatherName: familyData?.family_name || "",
        cnic: familyData?.cnic || "",
        mobile: familyData?.primary_mobile || "",
        gender: "Female",
        address: familyData?.address || "",
        class: "",
        section: "",
        studentId: "",
        familyCode: familyData?.family_code || "",
        availableSections: [],
        bFormNo: familyData?.cnic || "",
        familyStatus: "Family",
        isSaved: false,
      });
    }

    setStudents(newStudents);
    setActiveCardIndex(0);
  };

  // Generate student ID using PHP backend
  const handleGenerateStudentID = async (index) => {
    const student = students[index];
    const { class: className, section } = student;

    if (!className) {
      Swal.fire({
        icon: "warning",
        title: "Select Class First",
        text: "Please select a class before generating student ID.",
      });
      return;
    }

    if (!section) {
      Swal.fire({
        icon: "warning",
        title: "Select Section First",
        text: "Please select a section before generating student ID.",
      });
      return;
    }

    setIsLoading(true);
    try {
      const year = new Date().getFullYear();

      // Get next student ID from PHP backend
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=check-student-id`,
        {
          params: {
            class: className,
            year: year,
          },
        }
      );

      const { nextId } = response.data;

      if (nextId) {
        handleStudentChange(index, "studentId", nextId);
        Swal.fire({
          icon: "success",
          title: "Student ID Generated!",
          text: `Student ID: ${nextId}`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        // Fallback: generate ID locally
        const classShortForm = classShortForms[className] || "UNK";
        const yearPart = year.toString().slice(-2);
        const newId = `Std-${classShortForm}-${yearPart}001`;
        handleStudentChange(index, "studentId", newId);
        Swal.fire({
          icon: "info",
          title: "Generated ID",
          text: `Student ID: ${newId}`,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error("Error generating ID:", error);
      // Ultimate fallback
      const classShortForm = classShortForms[className] || "UNK";
      const yearPart = new Date().getFullYear().toString().slice(-2);
      const newId = `Std-${classShortForm}-${yearPart}001`;
      handleStudentChange(index, "studentId", newId);

      Swal.fire({
        icon: "warning",
        title: "Generated ID (Offline Mode)",
        text: `Student ID: ${newId}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save student to PHP backend
  const saveStudent = async (index) => {
    const student = students[index];

    // Validate required fields
    const requiredFields = [
      "name",
      "cnic",
      "mobile",
      "class",
      "section",
      "studentId",
    ];
    const missingFields = requiredFields.filter((field) => !student[field]);

    if (missingFields.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        html: `
          <div style="text-align: left;">
            <p>Please fill in the following required fields:</p>
            <ul style="margin-left: 20px; color: #e53e3e;">
              ${missingFields
                .map(
                  (field) =>
                    `<li>${field.replace(/([A-Z])/g, " $1").toUpperCase()}</li>`
                )
                .join("")}
            </ul>
          </div>
        `,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        name: student.name,
        fatherName: student.fatherName,
        cnic: student.cnic,
        mobile: student.mobile,
        gender: student.gender,
        address: student.address,
        class: student.class,
        section: student.section,
        studentId: student.studentId,
        bFormNo: student.bFormNo || student.cnic || "",
        familyStatus: "Family",
        familyCode: student.familyCode || familiesData?.family_code,
        family_name: familiesData?.family_name || "",
        bForm: student.cnic || student.bFormNo || "",
      };

      // Call PHP backend
      const response = await axios.post(
        `${API_BASE_URL}/students.php?action=add-family-student`,
        payload
      );

      // Mark student as saved
      setStudents((prev) =>
        prev.map((s, idx) => (idx === index ? { ...s, isSaved: true } : s))
      );

      // Update missing students info
      if (student.gender === "Male") {
        setMissingStudentsInfo((prev) => ({
          ...prev,
          missingBoys: Math.max(0, prev.missingBoys - 1),
          registeredBoys: prev.registeredBoys + 1,
          isComplete: prev.missingBoys - 1 <= 0 && prev.missingGirls <= 0,
        }));
      } else {
        setMissingStudentsInfo((prev) => ({
          ...prev,
          missingGirls: Math.max(0, prev.missingGirls - 1),
          registeredGirls: prev.registeredGirls + 1,
          isComplete: prev.missingBoys <= 0 && prev.missingGirls - 1 <= 0,
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Student Registered Successfully!",
        html: `
          <div style="text-align: left;">
            <p><strong>Student Name:</strong> ${student.name}</p>
            <p><strong>Student ID:</strong> ${student.studentId}</p>
            <p><strong>Class:</strong> ${student.class} - ${student.section}</p>
            <p><strong>Family:</strong> ${student.familyCode}</p>
            <p style="margin-top: 10px; color: #10b981;">
              ✅ Student has been added to the family successfully.
            </p>
          </div>
        `,
        timer: 2000,
        showConfirmButton: false,
      });

      // Move to next unsaved student if available
      const nextUnsavedIndex = students.findIndex(
        (s, idx) => idx > index && !s.isSaved
      );
      if (nextUnsavedIndex !== -1) {
        setActiveCardIndex(nextUnsavedIndex);
      } else {
        // Check if all students are saved
        const allSaved = students.every((s) => s.isSaved);
        if (allSaved && students.length > 0) {
          Swal.fire({
            icon: "success",
            title: "All Students Registered!",
            text: "All students have been registered successfully.",
            showConfirmButton: true,
          });
        }
      }
    } catch (error) {
      console.error("Save error:", error);

      let errorMessage = "Failed to save student data.";
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage =
            error.response.data.error || "Invalid data. Please check the form.";
        } else if (error.response.status === 409) {
          errorMessage =
            error.response.data.details ||
            "This student already exists in the system.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch autocomplete suggestions from PHP backend
  const fetchFamilyCodeSuggestions = async (query) => {
    if (query.length < 2) {
      setFamilyCodeSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=search-family`,
        { params: { type: "family-code", query: query } }
      );
      if (response.data.results) {
        setFamilyCodeSuggestions(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching family code suggestions:", error);
    }
  };

  const fetchFamilyNameSuggestions = async (query) => {
    if (query.length < 2) {
      setFamilyNamesSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=search-family`,
        { params: { type: "family-name", query: query } }
      );
      if (response.data.results) {
        setFamilyNamesSuggestions(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching family name suggestions:", error);
    }
  };

  const fetchCnicSuggestions = async (query) => {
    if (query.length < 2) {
      setCnicSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=search-family`,
        { params: { type: "cnic", query: query } }
      );
      if (response.data.results) {
        setCnicSuggestions(response.data.results);
      }
    } catch (error) {
      console.error("Error fetching CNIC suggestions:", error);
    }
  };

  // Handle student input changes
  const handleStudentChange = (index, field, value) => {
    const updatedStudents = [...students];
    updatedStudents[index][field] = value;
    setStudents(updatedStudents);

    // If class is changed, fetch sections
    if (field === "class") {
      fetchSections(value, index);
    }
  };

  // Handle class selection change
  const handleClassChange = async (index, className) => {
    handleStudentChange(index, "class", className);
  };

  // Handle section selection change
  const handleSectionChange = (index, section) => {
    handleStudentChange(index, "section", section);
  };

  // Handle family code change with suggestions
  const handleFamilyCodeChange = (e) => {
    const value = e.target.value;
    setFamilyCode(value);
    fetchFamilyCodeSuggestions(value);
  };

  // Handle family name change with suggestions
  const handleFamilyNameChange = (e) => {
    const value = e.target.value;
    setFamilyName(value);
    fetchFamilyNameSuggestions(value);
  };

  // Handle CNIC change with suggestions
  const handleCnicChange = (e) => {
    const value = e.target.value;
    setCnic(value);
    fetchCnicSuggestions(value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion, type) => {
    if (type === "familyCode") {
      setFamilyCode(suggestion.family_code);
      setFamilyCodeSuggestions([]);
    } else if (type === "familyName") {
      setFamilyName(suggestion.family_name);
      setFamilyNamesSuggestions([]);
    } else if (type === "cnic") {
      setCnic(suggestion.cnic);
      setCnicSuggestions([]);
    }
  };

  // Add one more student to existing family
  const addOneMoreStudent = () => {
    // Check if we can add more students
    const canAddMore =
      missingStudentsInfo.missingBoys > 0 ||
      missingStudentsInfo.missingGirls > 0;

    if (!canAddMore) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Add More Students",
        text: "This family has reached its maximum student capacity.",
      });
      return;
    }

    Swal.fire({
      title: "Add One More Student",
      html: `
        <div style="text-align: left;">
          <p><strong>Family:</strong> ${familiesData?.family_name} (${familiesData?.family_code})</p>
          <p><strong>Currently Registered:</strong> ${missingStudentsInfo.registeredBoys} boys, ${missingStudentsInfo.registeredGirls} girls</p>
          <p><strong>Family Total:</strong> ${familiesData?.boys} boys, ${familiesData?.girls} girls</p>
          <p><strong>Still Missing:</strong> ${missingStudentsInfo.missingBoys} boys, ${missingStudentsInfo.missingGirls} girls</p>
          <p>Select gender of the new student:</p>
        </div>
      `,
      input: "select",
      inputOptions: {
        Male: "Boy",
        Female: "Girl",
      },
      inputPlaceholder: "Select gender",
      showCancelButton: true,
      confirmButtonText: "Add Student Form",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "Please select a gender";
        }
        if (value === "Male" && missingStudentsInfo.missingBoys <= 0) {
          return "No more boys can be added (family limit reached)";
        }
        if (value === "Female" && missingStudentsInfo.missingGirls <= 0) {
          return "No more girls can be added (family limit reached)";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const gender = result.value;

        // Create new student form
        const newStudent = {
          id: Date.now(),
          name: "",
          fatherName: familiesData?.family_name || "",
          cnic: familiesData?.cnic || "",
          mobile: familiesData?.primary_mobile || "",
          gender: gender,
          address: familiesData?.address || "",
          class: "",
          section: "",
          studentId: "",
          familyCode: familiesData?.family_code || "",
          availableSections: [],
          bFormNo: familiesData?.cnic || "",
          familyStatus: "Family",
          isSaved: false,
        };

        setStudents((prev) => [...prev, newStudent]);
        setActiveCardIndex(students.length); // Go to new student

        Swal.fire({
          icon: "success",
          title: "Student Form Added",
          text: `Added form for a new ${gender.toLowerCase()} student.`,
          timer: 1500,
        });
      }
    });
  };

  // Refresh family data from backend
  const refreshFamilyData = async () => {
    if (!familiesData?.family_code) return;

    try {
      const response = await axios.get(
        `${API_BASE_URL}/students.php?action=check-family-duplicate`,
        {
          params: {
            family_code: familiesData.family_code,
          },
        }
      );

      if (response.data.exists) {
        const {
          family,
          missing_boys,
          missing_girls,
          registered_boys,
          registered_girls,
          is_complete,
        } = response.data;

        setFamiliesData(family);
        setMissingStudentsInfo((prev) => ({
          ...prev,
          missingBoys: missing_boys || 0,
          missingGirls: missing_girls || 0,
          registeredBoys: registered_boys || 0,
          registeredGirls: registered_girls || 0,
          isComplete: is_complete || false,
          totalBoys: family.boys || 0,
          totalGirls: family.girls || 0,
        }));
      }
    } catch (error) {
      console.error("Error refreshing family data:", error);
    }
  };

  // Clear all student forms
  const clearStudentForms = () => {
    setStudents([]);
    setActiveCardIndex(0);
  };

  // Reset form completely
  const resetForm = () => {
    Swal.fire({
      title: "Reset Form?",
      text: "This will clear all data including family search and student forms.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#718096",
      confirmButtonText: "Yes, reset everything",
      cancelButtonText: "No, keep data",
    }).then((result) => {
      if (result.isConfirmed) {
        setFamilyCode("");
        setFamilyName("");
        setCnic("");
        setFamiliesData(null);
        setStudents([]);
        setActiveCardIndex(0);
        setFamilyCodeSuggestions([]);
        setFamilyNamesSuggestions([]);
        setCnicSuggestions([]);
        setMissingStudentsInfo({
          missingBoys: 0,
          missingGirls: 0,
          registeredBoys: 0,
          registeredGirls: 0,
          isComplete: false,
          totalBoys: 0,
          totalGirls: 0,
        });
        Swal.fire("Reset!", "Form has been reset.", "success");
      }
    });
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    if (!familiesData) return 0;
    const total = familiesData.boys + familiesData.girls;
    const registered =
      missingStudentsInfo.registeredBoys + missingStudentsInfo.registeredGirls;
    return total > 0 ? Math.round((registered / total) * 100) : 0;
  };

  return (
    <div className="family-dashboard-wrapper">
      <Sidebar />

      <div className="family-content-wrapper family-fade-in">
        {/* Header */}
        <div className="family-page-header">
          <h1 className="family-page-title">
            <FiUsers className="family-icon-wrapper" />
            Family Student Registration
          </h1>
          <p className="family-page-subtitle">
            Register multiple students under a single family. Search for
            existing families or create new ones.
          </p>

          {/* Missing Students Info Bar */}
          {familiesData && (
            <div className="family-missing-info-bar">
              <div className="family-missing-stats">
                <span className="family-missing-stat">
                  <strong>Missing Boys:</strong>{" "}
                  {missingStudentsInfo.missingBoys}
                </span>
                <span className="family-missing-stat">
                  <strong>Missing Girls:</strong>{" "}
                  {missingStudentsInfo.missingGirls}
                </span>
                <span className="family-missing-stat">
                  <strong>Registered:</strong>{" "}
                  {missingStudentsInfo.registeredBoys +
                    missingStudentsInfo.registeredGirls}{" "}
                  / {familiesData?.boys + familiesData?.girls}
                </span>
                <span className="family-missing-stat">
                  <strong>Progress:</strong> {calculateCompletion()}%
                </span>
              </div>
              {missingStudentsInfo.missingBoys > 0 ||
              missingStudentsInfo.missingGirls > 0 ? (
                <button
                  className="family-btn-add-more"
                  onClick={addOneMoreStudent}
                  disabled={isLoading || isSubmitting}
                >
                  <FiPlus /> Add One More Student
                </button>
              ) : (
                <div className="family-complete-badge">
                  <FiCheck /> All Students Registered
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search Section */}
        <section className="family-search-section">
          <h2 className="family-section-title">
            <FiSearch className="family-section-icon" />
            Find or Create Family
          </h2>

          <div className="family-search-grid">
            {/* Family Code Input */}
            <div className="family-input-group">
              <label className="family-input-label">
                <FiCreditCard /> Family Code
              </label>
              <input
                type="text"
                value={familyCode}
                onChange={handleFamilyCodeChange}
                placeholder="Enter family code (e.g., FAM-001)"
                className="family-search-input"
                disabled={isLoading}
              />
              {familyCodeSuggestions.length > 0 && (
                <div className="family-suggestion-list">
                  {familyCodeSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="family-suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(suggestion, "familyCode")
                      }
                    >
                      <FiCreditCard className="family-suggestion-icon" />
                      <div>
                        <div className="family-suggestion-main">
                          {suggestion.family_code}
                        </div>
                        <div className="family-suggestion-sub">
                          {suggestion.family_name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Family Name Input */}
            <div className="family-input-group">
              <label className="family-input-label">
                <FiUser /> Family Name
              </label>
              <input
                type="text"
                value={familyName}
                onChange={handleFamilyNameChange}
                placeholder="Enter family name"
                className="family-search-input"
                disabled={isLoading}
              />
              {familyNamesSuggestions.length > 0 && (
                <div className="family-suggestion-list">
                  {familyNamesSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="family-suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(suggestion, "familyName")
                      }
                    >
                      <FiUsers className="family-suggestion-icon" />
                      <div>
                        <div className="family-suggestion-main">
                          {suggestion.family_name}
                        </div>
                        <div className="family-suggestion-sub">
                          {suggestion.family_code}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CNIC Input */}
            <div className="family-input-group">
              <label className="family-input-label">
                <FiCreditCard /> CNIC / B-Form
              </label>
              <input
                type="text"
                value={cnic}
                onChange={handleCnicChange}
                placeholder="Enter CNIC (35202-1234567-1)"
                className="family-search-input"
                disabled={isLoading}
              />
              {cnicSuggestions.length > 0 && (
                <div className="family-suggestion-list">
                  {cnicSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="family-suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion, "cnic")}
                    >
                      <FiUser className="family-suggestion-icon" />
                      <div>
                        <div className="family-suggestion-main">
                          {suggestion.cnic}
                        </div>
                        <div className="family-suggestion-sub">
                          {suggestion.family_name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="family-search-actions">
            <button
              type="button"
              className="family-btn-primary"
              onClick={searchFamilies}
              disabled={isLoading || (!familyCode && !familyName && !cnic)}
            >
              {isLoading ? (
                <>
                  <FiRefreshCw className="family-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <FiSearch />
                  Search Family
                </>
              )}
            </button>
            <button
              type="button"
              className="family-btn-secondary"
              onClick={refreshFamilyData}
              disabled={!familiesData || isLoading}
            >
              <FiRefreshCw />
              Refresh Data
            </button>
            <button
              type="button"
              className="family-btn-secondary"
              style={{ background: "gold" }}
              onClick={resetForm}
            >
              <FiX />
              Reset All
            </button>
          </div>
        </section>

        {/* Family Data Section */}
        {familiesData && (
          <section className="family-data-section">
            <div className="family-section-header">
              <FiHome className="family-section-icon" />
              <h2 className="family-section-title">Family Details</h2>
            </div>

            <div className="family-table-responsive">
              <table className="family-data-table">
                <thead>
                  <tr>
                    <th>Family Code</th>
                    <th>Family Name</th>
                    <th>Primary Mobile</th>
                    <th>CNIC</th>
                    <th>Address</th>
                    <th>Student Count</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>{familiesData.family_code}</strong>
                    </td>
                    <td>{familiesData.family_name}</td>
                    <td>
                      <div className="family-mobile-display">
                        <FiPhone />
                        {familiesData.primary_mobile || "Not Provided"}
                      </div>
                    </td>
                    <td>{familiesData.cnic || "Not Provided"}</td>
                    <td>{familiesData.address || "Not Provided"}</td>
                    <td>
                      <div className="family-student-count">
                        <span className="family-boy-count">
                          {familiesData.boys} boys
                        </span>
                        <span className="family-girl-count">
                          {familiesData.girls} girls
                        </span>
                        <span className="family-total-count">
                          Total: {familiesData.total_students}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`family-status-badge ${
                          missingStudentsInfo.isComplete
                            ? "family-status-complete"
                            : "family-status-incomplete"
                        }`}
                      >
                        {missingStudentsInfo.isComplete
                          ? "Complete"
                          : "Incomplete"}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Registration Progress */}
            <div className="family-registration-progress">
              <div className="family-progress-bar-container">
                <div className="family-progress-label">
                  Registration Progress:
                  <span className="family-progress-percentage">
                    {calculateCompletion()}%
                  </span>
                </div>
                <div className="family-progress-bar">
                  <div
                    className="family-progress-fill"
                    style={{
                      width: `${calculateCompletion()}%`,
                    }}
                  ></div>
                </div>
                <div className="family-progress-details">
                  <div className="family-progress-detail">
                    <span className="family-progress-label">Boys:</span>
                    <span className="family-progress-value">
                      {missingStudentsInfo.registeredBoys} / {familiesData.boys}{" "}
                      registered
                      {familiesData.boys > 0 &&
                        ` (${Math.round(
                          (missingStudentsInfo.registeredBoys /
                            familiesData.boys) *
                            100
                        )}%)`}
                    </span>
                  </div>
                  <div className="family-progress-detail">
                    <span className="family-progress-label">Girls:</span>
                    <span className="family-progress-value">
                      {missingStudentsInfo.registeredGirls} /{" "}
                      {familiesData.girls} registered
                      {familiesData.girls > 0 &&
                        ` (${Math.round(
                          (missingStudentsInfo.registeredGirls /
                            familiesData.girls) *
                            100
                        )}%)`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Student Cards Section */}
        {students.length > 0 && (
          <section className="family-student-cards-section">
            <div className="family-cards-header">
              <div>
                <h2 className="family-section-title">
                  <FiUsers className="family-section-icon" />
                  Student Registration ({students.length} Forms)
                </h2>
                <p className="family-page-subtitle">
                  Fill student details below. Required fields are marked with *
                </p>
              </div>
              <div className="family-cards-count">
                Form {activeCardIndex + 1} of {students.length}
              </div>
            </div>

            {/* Card Navigation */}
            <div className="family-card-navigation">
              <button
                className="family-btn-nav"
                onClick={() =>
                  setActiveCardIndex((prev) => Math.max(0, prev - 1))
                }
                disabled={activeCardIndex === 0 || isSubmitting}
              >
                <FiChevronLeft />
                Previous
              </button>

              <div className="family-card-indicators">
                {students.map((student, index) => (
                  <button
                    key={student.id}
                    onClick={() => setActiveCardIndex(index)}
                    className={`family-card-indicator ${
                      index === activeCardIndex ? "family-active-indicator" : ""
                    } ${
                      student.isSaved
                        ? "family-saved-indicator"
                        : student.studentId
                        ? "family-completed-indicator"
                        : "family-pending-indicator"
                    }`}
                    disabled={isSubmitting}
                  >
                    {student.isSaved ? "✓" : index + 1}
                  </button>
                ))}
              </div>

              <button
                className="family-btn-nav"
                onClick={() =>
                  setActiveCardIndex((prev) =>
                    Math.min(students.length - 1, prev + 1)
                  )
                }
                disabled={
                  activeCardIndex === students.length - 1 || isSubmitting
                }
              >
                Next
                <FiChevronRight />
              </button>
            </div>

            {/* Active Student Card */}
            <div className="family-cards-grid">
              {students.map((student, index) => (
                <div
                  key={student.id}
                  className={`family-student-card ${
                    index === activeCardIndex
                      ? "family-active-card"
                      : "family-inactive-card"
                  } ${student.isSaved ? "family-saved-card" : ""}`}
                  onClick={() => !isSubmitting && setActiveCardIndex(index)}
                >
                  <div className="family-card-header">
                    <div>
                      <h3 className="family-card-title">
                        {student.isSaved ? "✓ " : ""}Student {index + 1} -{" "}
                        {student.gender}
                        {student.isSaved && (
                          <span className="family-saved-label"> (Saved)</span>
                        )}
                      </h3>
                      <div className="family-card-subtitle">
                        <span
                          className={`family-gender-badge ${
                            student.gender === "Male"
                              ? "family-badge-boy"
                              : "family-badge-girl"
                          }`}
                        >
                          {student.gender}
                        </span>
                        <span className="family-card-family-code">
                          Family: {student.familyCode}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`family-card-badge ${
                        student.isSaved
                          ? "family-badge-saved"
                          : student.studentId
                          ? "family-badge-completed"
                          : "family-badge-pending"
                      }`}
                    >
                      {student.isSaved
                        ? "✓ Saved"
                        : student.studentId
                        ? "✓ ID Ready"
                        : "Need ID"}
                    </div>
                  </div>

                  {index === activeCardIndex && !student.isSaved && (
                    <div className="family-card-form">
                      {/* Student Name */}
                      <div className="family-form-group">
                        <label className="family-form-label">
                          Student Name *
                        </label>
                        <input
                          type="text"
                          value={student.name}
                          onChange={(e) =>
                            handleStudentChange(index, "name", e.target.value)
                          }
                          placeholder="Enter student full name"
                          className="family-form-input"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Father's Name */}
                      <div className="family-form-group">
                        <label className="family-form-label">
                          Father's Name
                        </label>
                        <input
                          type="text"
                          value={student.fatherName}
                          onChange={(e) =>
                            handleStudentChange(
                              index,
                              "fatherName",
                              e.target.value
                            )
                          }
                          className="family-form-input"
                          readOnly={!!student.fatherName}
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* CNIC/B-Form */}
                      <div className="family-form-group">
                        <label className="family-form-label">
                          CNIC / B-Form *
                        </label>
                        <input
                          type="text"
                          value={student.cnic}
                          onChange={(e) =>
                            handleStudentChange(index, "cnic", e.target.value)
                          }
                          placeholder="Format: 35202-1234567-1"
                          className="family-form-input"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Mobile */}
                      <div className="family-form-group">
                        <label className="family-form-label">
                          Mobile Number *
                        </label>
                        <input
                          type="text"
                          value={student.mobile}
                          onChange={(e) =>
                            handleStudentChange(index, "mobile", e.target.value)
                          }
                          placeholder="Format: 0300-1234567"
                          className="family-form-input"
                          required
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Address */}
                      <div className="family-form-group family-full-width">
                        <label className="family-form-label">Address</label>
                        <textarea
                          value={student.address}
                          onChange={(e) =>
                            handleStudentChange(
                              index,
                              "address",
                              e.target.value
                            )
                          }
                          placeholder="Enter complete address"
                          className="family-form-textarea"
                          rows="3"
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Class & Section */}
                      <div className="family-form-group">
                        <label className="family-form-label">Class *</label>
                        <select
                          value={student.class}
                          onChange={(e) =>
                            handleClassChange(index, e.target.value)
                          }
                          className="family-form-select"
                          required
                          disabled={isSubmitting}
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls, i) => (
                            <option key={i} value={cls}>
                              {cls}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="family-form-group">
                        <label className="family-form-label">Section *</label>
                        <select
                          value={student.section}
                          onChange={(e) =>
                            handleSectionChange(index, e.target.value)
                          }
                          className="family-form-select"
                          disabled={!student.class || isSubmitting}
                          required
                        >
                          <option value="">Select Section</option>
                          {student.availableSections?.map((sec, i) => (
                            <option key={i} value={sec.section_hint || sec}>
                              {sec.section_name || sec} (
                              {sec.section_hint || sec})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Student ID Generator */}
                      <div className="family-form-group family-full-width">
                        <label className="family-form-label">
                          Student ID *
                        </label>
                        <div className="family-id-generator">
                          <input
                            type="text"
                            value={student.studentId}
                            className="family-form-input"
                            readOnly
                            placeholder="Generate ID after selecting class & section"
                            disabled={isSubmitting}
                          />
                          <button
                            type="button"
                            className="family-btn-generate"
                            onClick={() => handleGenerateStudentID(index)}
                            disabled={
                              !student.class ||
                              !student.section ||
                              isLoading ||
                              isSubmitting
                            }
                          >
                            {isLoading ? (
                              <FiRefreshCw className="family-spin" />
                            ) : (
                              <FiRefreshCw />
                            )}
                            {isLoading ? "Generating..." : "Generate ID"}
                          </button>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="family-card-actions">
                        <button
                          type="button"
                          className="family-btn-save"
                          onClick={() => saveStudent(index)}
                          disabled={isSubmitting || !student.studentId}
                        >
                          {isSubmitting ? (
                            <>
                              <FiRefreshCw className="family-spin" />
                              Registering...
                            </>
                          ) : (
                            <>
                              <FiSave />
                              Register Student {index + 1}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {index === activeCardIndex && student.isSaved && (
                    <div className="family-saved-message">
                      <div className="family-saved-icon">
                        <FiCheck />
                      </div>
                      <div className="family-saved-content">
                        <h4>Student Registered Successfully!</h4>
                        <p>
                          <strong>Name:</strong> {student.name}
                        </p>
                        <p>
                          <strong>Student ID:</strong> {student.studentId}
                        </p>
                        <p>
                          <strong>Class:</strong> {student.class} -{" "}
                          {student.section}
                        </p>
                        <p>This student has been registered in the database.</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <div className="family-progress-section">
              <div className="family-progress-bar">
                <div
                  className="family-progress-fill"
                  style={{
                    width: `${
                      ((activeCardIndex + 1) / students.length) * 100
                    }%`,
                  }}
                />
              </div>
              <div className="family-progress-info">
                <span>
                  Forms: {activeCardIndex + 1} of {students.length}(
                  {Math.round(((activeCardIndex + 1) / students.length) * 100)}
                  %)
                </span>
                <span>Saved: {students.filter((s) => s.isSaved).length}</span>
                <span>
                  Pending: {students.filter((s) => !s.isSaved).length}
                </span>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AddFamilyStudent;
